import { Link } from "react-router-dom";
import { Trash2, Scale, ShieldAlert } from "lucide-react";
import type { RuleEvaluationItem } from "../../gen/models";
import { EntityLinkedText } from "../evaluation/EntityLinkedText";
import { parseReasoningToDetails } from "../evaluation/parseReasoningToDetails";

interface BatchCommentsTabProps {
  comments: any[];
  onRemoveComment: (commentId: string) => void;
}

export function BatchCommentsTab({ comments, onRemoveComment }: BatchCommentsTabProps) {
  if (!comments || comments.length === 0) {
    return <div className="p-8 text-center text-xs text-stone-400">No comments assigned to this batch.</div>;
  }

  return (
    <div className="space-y-3">
      {comments.map((c) => (
        <div
          key={c.id}
          className="p-3.5 rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 space-y-2 text-xs"
        >
          <div className="flex items-center justify-between text-[11px] text-stone-400">
            <div className="flex items-center gap-2">
              {c.created_at && (
                <span>
                  {new Date(c.created_at).toLocaleDateString([], { month: "short", day: "numeric" })}{" "}
                  {new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              {c.thread_id && (
                <Link
                  to={`/threads/${c.thread_id}`}
                  className="text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-0.5"
                >
                  <span>{c.thread_title || `Thread #${c.thread_id.slice(0, 8)}`}</span>
                </Link>
              )}
            </div>
            <button
              type="button"
              onClick={() => onRemoveComment(c.id)}
              className="text-stone-400 hover:text-rose-600 transition"
              title="Remove comment from batch"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {c.selected_text && (
            <div className="p-2 rounded bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-900 dark:text-amber-200 italic line-clamp-2">
              "{c.selected_text}"
            </div>
          )}

          <p className="text-stone-800 dark:text-stone-200 leading-relaxed whitespace-pre-wrap">
            {c.comment_text}
          </p>

          {((c.rule_ids && c.rule_ids.length > 0) || (c.knowledge_item_ids && c.knowledge_item_ids.length > 0)) && (
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-stone-100 dark:border-stone-800">
              {c.rule_ids?.map((ruleId: string) => (
                <Link
                  key={ruleId}
                  to={`/rules/${ruleId}`}
                  className="text-[11px] font-mono text-stone-600 dark:text-stone-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:underline transition"
                  title="View referenced rule"
                >
                  <span>Rule #{ruleId.slice(0, 8)}</span>
                </Link>
              ))}
              {c.knowledge_item_ids?.map((itemId: string) => (
                <Link
                  key={itemId}
                  to={`/knowledge/${itemId}`}
                  className="text-[11px] font-mono text-stone-600 dark:text-stone-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:underline transition"
                  title="View referenced knowledge item"
                >
                  <span>Doc #{itemId.slice(0, 8)}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

interface BatchEvaluationsTabProps {
  evaluations: any[];
  onRemoveEvaluation: (evaluationId: string) => void;
}

export function BatchEvaluationsTab({ evaluations, onRemoveEvaluation }: BatchEvaluationsTabProps) {
  if (!evaluations || evaluations.length === 0) {
    return <div className="p-8 text-center text-xs text-stone-400">No evaluations assigned to this batch.</div>;
  }

  return (
    <div className="space-y-3">
      {evaluations.map((ev) => (
        <div
          key={ev.id}
          className="p-3.5 rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 space-y-2 text-xs"
        >
          <div className="flex items-center justify-between text-[11px]">
            <div className="flex items-center gap-2">
              <span className="font-bold text-stone-700 dark:text-stone-300">
                {ev.evaluator_type === "human" ? "Human Reviewer" : "Automated Judge"}
              </span>
              <span className="px-1.5 py-0.2 rounded font-mono font-bold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300">
                Score: {Number(ev.score).toFixed(2)}
              </span>
              {ev.status === "flagged" && (
                <span className="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                  Flagged
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => onRemoveEvaluation(ev.id)}
              className="text-stone-400 hover:text-rose-600 transition"
              title="Remove evaluation from batch"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {(() => {
            const details = ev.details || parseReasoningToDetails(ev.reasoning);
            const rawRules: RuleEvaluationItem[] = details?.rule_evaluations || [];
            const violations = rawRules.filter((r: RuleEvaluationItem) => r.status === "violated");
            const textToDisplay = details?.summary || ev.reasoning;

            return (
              <div className="space-y-2">
                {textToDisplay && (
                  <p className="text-stone-700 dark:text-stone-300 leading-relaxed text-xs">
                    <EntityLinkedText text={textToDisplay} />
                  </p>
                )}
                {violations.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-[10px] uppercase font-bold text-rose-600 dark:text-rose-400 flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" />
                      Violations:
                    </span>
                    {violations.map((v: RuleEvaluationItem, i: number) => (
                      <Link
                        key={`${v.rule_id}-${i}`}
                        to={`/rules/${v.rule_id}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900/60 hover:bg-rose-100 dark:hover:bg-rose-900/80 transition"
                        title={v.explanation}
                      >
                        <Scale className="w-3 h-3 text-rose-500 shrink-0" />
                        <span>{v.rule_title || `Rule [${v.rule_id.slice(0, 8)}]`}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {ev.thread_id && (
            <div className="pt-1">
              <Link
                to={`/threads/${ev.thread_id}`}
                className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
              >
                <span>
                  {ev.thread_title
                    ? `Inspect: ${ev.thread_title}`
                    : `Inspect Thread #${ev.thread_id.slice(0, 8)}`}
                </span>
              </Link>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
